import { currentVisitorId } from './visitor';
import { isTestVisit } from './testVisit';

/**
 * V6's A/B test (docs/superpowers/specs/2026-10-14-v6-design.md), on the demo screen:
 *   a: the button builds at once (as before V6);
 *   b: the button asks one question first, and the answer shapes the build.
 *
 * A visitor joins when they first see a demo (an ad visit): `variant()` puts them in a group by a
 * hash of their visitor id (analytics/visitor.ts), half and half, and keeps it in localStorage so a
 * later visit is in the same group even if the id were lost. Somebody with no demo never joins, and
 * their events carry no variant. A test visit (`test=1`) may choose its group with `?variant=a|b`,
 * for the rest of the tab; a real visitor's address cannot change theirs.
 *
 * Every funnel event a member sends carries `variant` (analytics/funnel.ts `assignedVariants`), to our
 * own ingest only, so the two groups can be counted apart from the first step to the last.
 */
export type Experiment = 'demo_question';
export type Variant = 'a' | 'b';

const key = (name: Experiment): string => `saddle.exp.${name}`;
const isVariant = (v: unknown): v is Variant => v === 'a' || v === 'b';

/** This page's memory, for a browser whose storage refuses: the group holds for the page load. */
const heldHere = new Map<Experiment, Variant>();

/*
 * FNV-1a over the id with the experiment's name, so a later experiment splits people differently.
 * Its lowest bit is the group: even 'a', odd 'b'.
 */
export function variantForVisitor(visitorId: string, name: Experiment = 'demo_question'): Variant {
  const text = `${name}:${visitorId}`;
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  // Fold the high half in: FNV's lowest bit alone follows the last character too closely.
  h ^= h >>> 16;
  return (h & 1) === 0 ? 'a' : 'b';
}

/** A test visit's chosen group: from the address, then from this tab's memory of it. */
function testOverride(name: Experiment): Variant | null {
  if (!isTestVisit()) return null;
  try {
    const asked = new URLSearchParams(typeof location !== 'undefined' ? location.search : '').get('variant');
    if (isVariant(asked)) {
      sessionStorage.setItem(key(name), asked);
      return asked;
    }
    const kept = sessionStorage.getItem(key(name));
    return isVariant(kept) ? kept : null;
  } catch {
    return null;
  }
}

/** The group this visitor is in, or null when they have not joined. Never puts them in one. */
export function assignedVariant(name: Experiment): Variant | null {
  const forced = testOverride(name);
  if (forced) return forced;
  try {
    const kept = localStorage.getItem(key(name));
    if (isVariant(kept)) return kept;
  } catch {
    /* fall through to this page's memory */
  }
  return heldHere.get(name) ?? null;
}

/** The group this visitor is in, joining them to it now if they were not. */
export function variant(name: Experiment): Variant {
  const known = assignedVariant(name);
  if (known) return known;
  const chosen = variantForVisitor(currentVisitorId(), name);
  heldHere.set(name, chosen);
  try {
    localStorage.setItem(key(name), chosen);
  } catch {
    /* held for this page load only */
  }
  return chosen;
}

/** What every funnel event carries for the experiments this visitor is in (one, today). */
export function assignedVariants(): { variant?: Variant } {
  const v = assignedVariant('demo_question');
  return v ? { variant: v } : {};
}

/** Test-only: forget this page's memory of any group. */
export function _resetExperimentsForTest(): void {
  heldHere.clear();
}
